import type { FinderFilters, FinderSort, Gender, ProfileType } from "@amtana/finder-contract";
import type { Catalogs, Formats, PlainKey, Translate, Translation } from "@amtana/i18n/catalog";

// The Account Finder's words, in English and Spanish: one key per string, read
// through `useFinderCopy` (behavior/use-finder-copy.ts). The contract names
// genders, profile types and languages in English; the tables below hold the
// keys that read them in the active language. Also the form's own shape.

export type FinderCopyKey = keyof typeof EN;
export type FinderCatalog = typeof EN;

const EN = {
  finderScreenTitle: "Account Finder · {product}",
  accountScreenTitle: "Account · {product}",
  navAccounts: "Accounts",
  breadcrumbHome: "Home",
  breadcrumbFinder: "Account Finder",
  finderTitle: "Account Finder",
  finderDescription: "Search and discover accounts from our database",
  savedSearchesButton: "Saved searches ({count})",
  updateTooltip: 'Update "{name}" with current filters',
  update: "Update",
  searchUpdated: "Search updated successfully",
  couldNotUpdateSearch: "Could not update the search",
  saveSearch: "Save search",
  searchWithAi: "Search with AI",
  aiInterpreted: "The AI has interpreted your search as:",
  saveThisSearch: "Save this search",
  searchSaved: "Search saved successfully",
  couldNotSaveSearch: "Could not save the search",
  allGenders: "All genders",
  genderMale: "Male",
  genderFemale: "Female",
  genderUnknown: "Unknown",
  genderBrand: "Brand",
  genderGroup: "Group",
  genderCouple: "Couple",
  genderFamily: "Family",
  genderBand: "Band",
  profileCreator: "Creator",
  profileBusiness: "Business",
  profilePersonal: "Personal",
  sortFollowers: "Followers",
  sortEngagement: "Engagement",
  sortAvgLikes: "Avg. Likes",
  aiExampleFitness: "Female fitness influencers with more than 100k followers",
  aiExampleFoodies: "Foodies in Mexico with good engagement",
  aiExampleBeauty: "Beauty micro influencers with email",
  languageEn: "English",
  languageEs: "Spanish",
  languagePt: "Portuguese",
  languageIt: "Italian",
  languageFr: "French",
  languageDe: "German",
  languageNl: "Dutch",
  languageTr: "Turkish",
  languagePl: "Polish",
  languageAr: "Arabic",
  aiTitle: "AI Search",
  aiDescription:
    "Describe in natural language what type of influencers you're looking for and the AI will automatically generate the filters.",
  aiSearching: "Searching...",
  aiQueryLabel: "What are you looking for?",
  aiPlaceholder: "E.g.: Fashion influencers in Spain with more than 50k followers and email...",
  aiExamples: "Search examples:",
  couldNotRunAi: "We couldn't run the AI search",
  audienceTitle: "Audience Data",
  audienceUnavailable: "Audience data is not available for this influencer",
  topCountries: "Top Countries",
  topCities: "Top Cities",
  ageGender: "Age & Gender",
  genderSplit: "{female}% female · {male}% male",
  ageGenderLabel: "{range}: {female}% female, {male}% male",
  female: "Female",
  male: "Male",
  audienceTypes: "Audience Types",
  audienceTypesLabel: "Audience types",
  typeReal: "Real {percent}%",
  typeInfluencers: "Influencers {percent}%",
  typeSuspicious: "Suspicious {percent}%",
  typeMassFollowers: "Mass Followers {percent}%",
  filtersTitle: "Filters",
  clearFilters: "Clear filters",
  filterFollowers: "Followers",
  filterCategory: "Category",
  allCategories: "All categories",
  filterPosts: "Posts",
  filterLanguage: "Language",
  allLanguages: "All languages",
  filterGender: "Gender",
  filterBio: "Bio contains",
  bioPlaceholder: "e.g. fitness, agency",
  filterUsername: "Username",
  usernamePlaceholder: "e.g. maria",
  filterAvgLikes: "Avg. Likes",
  filterAvgComments: "Avg. Comments",
  filterEngagementRate: "Engagement Rate (%)",
  filterTags: "Tags",
  allTags: "All tags",
  filterCountry: "Country",
  allCountries: "All countries",
  filterProfileType: "Profile Type",
  allTypes: "All types",
  rangeMin: "Min{unit}",
  rangeMax: "Max{unit}",
  hasEmail: "Has email",
  hasWebsite: "Has website",
  hasPhone: "Has phone",
  verifiedOnly: "Verified only",
  hideAdvanced: "Hide advanced",
  advanced: "Advanced",
  resultsTitle: "Results",
  sortBy: "Sort by",
  descending: "Descending",
  ascending: "Ascending",
  useFilters: "Use the filters above to find accounts",
  accountsLabel: "Accounts",
  limitReachedTitle: "Results limit reached",
  limitReachedDescription:
    "Your plan allows viewing up to {limit} results. Upgrade your plan to access all {total} results.",
  upgradePlan: "Upgrade Plan",
  noAccounts: "No accounts found matching your criteria",
  columnAccount: "Account",
  columnLang: "Lang",
  columnFollowers: "Followers",
  columnBio: "Bio",
  columnCategory: "Category",
  columnEmail: "Email",
  columnWeb: "Web",
  view: "View",
  verifiedAccount: "Verified account",
  savedSearchesTitle: "Saved Searches",
  savedSearchesDescription: "Your saved searches",
  close: "Close",
  noSavedSearches: "You don't have any saved searches yet",
  updatedOn: "Updated {date}",
  loadSearch: "Load search",
  actionsLabel: "Actions for {name}",
  renameSearch: "Rename search",
  deleteSearch: "Delete search",
  searchDeleted: "Search deleted successfully",
  couldNotDeleteSearch: "Could not delete the search",
  searchRenamed: "Search renamed successfully",
  couldNotRenameSearch: "Could not rename the search",
  cancel: "Cancel",
  saving: "Saving...",
  save: "Save",
  searchName: "Search name",
  searchNamePlaceholder: "e.g. Spanish fitness influencers",
  nameTooShort: "Name must be at least 3 characters",
  nameTooLong: "Name must be at most 50 characters",
  nameInvalid: "Name contains invalid characters",
  influencerNotFound: "Influencer not found",
  backToSearch: "Back to search",
  contactEmail: "Email",
  contactPhone: "Phone",
  contactWebsite: "Website",
  viewOnInstagram: "View on Instagram",
  analyzing: "Analyzing...",
  analyzeFollowers: "Analyze Followers",
  verified: "Verified",
  private: "Private",
  public: "Public",
  statFollowers: "Followers",
  statFollowing: "Following",
  statPosts: "Posts",
  contactInformation: "Contact Information",
  noContactInformation: "No contact information available",
  categoriesAndTags: "Categories & Tags",
  detailCategory: "Category",
  detailSubcategories: "Subcategories",
  detailTags: "Tags",
  profileDetails: "Profile Details",
  detailCountry: "Country",
  detailLanguage: "Language",
  detailGender: "Gender",
  detailProfileType: "Profile Type",
  detailAgeRange: "Age Range",
  detailAccountStatus: "Account Status",
  performanceMetrics: "Performance Metrics",
  metricAvgLikes: "Avg. Likes",
  metricAvgComments: "Avg. Comments",
  metricAvgViews: "Avg. Views",
  metricEngagementRate: "Engagement Rate",
  metricQualityScore: "Quality Score",
  metricFakeFollowers: "Fake Followers",
  largeAccountTitle: "Large account",
  largeAccountDescription:
    "Accounts with more than {limit} followers tend to have less targeted audiences. We recommend analyzing smaller accounts for better results.",
  analyzeAnyway: "Analyze anyway",
  couldNotAnalyze: "Could not create the analysis",
} as const;

const ES: Translation<FinderCatalog> = {
  finderScreenTitle: "Buscador de cuentas · {product}",
  accountScreenTitle: "Cuenta · {product}",
  navAccounts: "Cuentas",
  breadcrumbHome: "Inicio",
  breadcrumbFinder: "Buscador de cuentas",
  finderTitle: "Buscador de cuentas",
  finderDescription: "Busca y descubre cuentas en nuestra base de datos",
  savedSearchesButton: "Búsquedas guardadas ({count})",
  updateTooltip: 'Actualizar "{name}" con los filtros actuales',
  update: "Actualizar",
  searchUpdated: "Búsqueda actualizada con éxito",
  couldNotUpdateSearch: "No se pudo actualizar la búsqueda",
  saveSearch: "Guardar búsqueda",
  searchWithAi: "Buscar con IA",
  aiInterpreted: "La IA interpretó tu búsqueda así:",
  saveThisSearch: "Guardar esta búsqueda",
  searchSaved: "Búsqueda guardada con éxito",
  couldNotSaveSearch: "No se pudo guardar la búsqueda",
  allGenders: "Todos los géneros",
  genderMale: "Masculino",
  genderFemale: "Femenino",
  genderUnknown: "Desconocido",
  genderBrand: "Marca",
  genderGroup: "Grupo",
  genderCouple: "Pareja",
  genderFamily: "Familia",
  genderBand: "Banda",
  profileCreator: "Creador",
  profileBusiness: "Negocio",
  profilePersonal: "Personal",
  sortFollowers: "Seguidores",
  sortEngagement: "Interacción",
  sortAvgLikes: "Prom. de me gusta",
  aiExampleFitness: "Influencers mujeres de fitness con más de 100k seguidores",
  aiExampleFoodies: "Amantes de la comida en México con buena interacción",
  aiExampleBeauty: "Microinfluencers de belleza con correo",
  languageEn: "Inglés",
  languageEs: "Español",
  languagePt: "Portugués",
  languageIt: "Italiano",
  languageFr: "Francés",
  languageDe: "Alemán",
  languageNl: "Neerlandés",
  languageTr: "Turco",
  languagePl: "Polaco",
  languageAr: "Árabe",
  aiTitle: "Búsqueda con IA",
  aiDescription:
    "Describe con tus palabras qué tipo de influencers buscas y la IA generará los filtros automáticamente.",
  aiSearching: "Buscando...",
  aiQueryLabel: "¿Qué estás buscando?",
  aiPlaceholder: "Ej.: Influencers de moda en España con más de 50k seguidores y correo...",
  aiExamples: "Ejemplos de búsqueda:",
  couldNotRunAi: "No pudimos hacer la búsqueda con IA",
  audienceTitle: "Datos de la audiencia",
  audienceUnavailable: "Los datos de audiencia no están disponibles para este influencer",
  topCountries: "Países principales",
  topCities: "Ciudades principales",
  ageGender: "Edad y género",
  genderSplit: "{female}% mujeres · {male}% hombres",
  ageGenderLabel: "{range}: {female}% mujeres, {male}% hombres",
  female: "Mujeres",
  male: "Hombres",
  audienceTypes: "Tipos de audiencia",
  audienceTypesLabel: "Tipos de audiencia",
  typeReal: "Reales {percent}%",
  typeInfluencers: "Influencers {percent}%",
  typeSuspicious: "Sospechosos {percent}%",
  typeMassFollowers: "Seguidores masivos {percent}%",
  filtersTitle: "Filtros",
  clearFilters: "Borrar filtros",
  filterFollowers: "Seguidores",
  filterCategory: "Categoría",
  allCategories: "Todas las categorías",
  filterPosts: "Publicaciones",
  filterLanguage: "Idioma",
  allLanguages: "Todos los idiomas",
  filterGender: "Género",
  filterBio: "La biografía contiene",
  bioPlaceholder: "p. ej. fitness, agencia",
  filterUsername: "Usuario",
  usernamePlaceholder: "p. ej. maria",
  filterAvgLikes: "Prom. de me gusta",
  filterAvgComments: "Prom. de comentarios",
  filterEngagementRate: "Tasa de interacción (%)",
  filterTags: "Etiquetas",
  allTags: "Todas las etiquetas",
  filterCountry: "País",
  allCountries: "Todos los países",
  filterProfileType: "Tipo de perfil",
  allTypes: "Todos los tipos",
  rangeMin: "Mín{unit}",
  rangeMax: "Máx{unit}",
  hasEmail: "Tiene correo",
  hasWebsite: "Tiene sitio web",
  hasPhone: "Tiene teléfono",
  verifiedOnly: "Solo verificados",
  hideAdvanced: "Ocultar avanzado",
  advanced: "Avanzado",
  resultsTitle: "Resultados",
  sortBy: "Ordenar por",
  descending: "Descendente",
  ascending: "Ascendente",
  useFilters: "Usa los filtros de arriba para encontrar cuentas",
  accountsLabel: "Cuentas",
  limitReachedTitle: "Llegaste al límite de resultados",
  limitReachedDescription:
    "Tu plan permite ver hasta {limit} resultados. Mejora tu plan para acceder a los {total} resultados.",
  upgradePlan: "Mejorar plan",
  noAccounts: "No hay cuentas que coincidan con tu búsqueda",
  columnAccount: "Cuenta",
  columnLang: "Idioma",
  columnFollowers: "Seguidores",
  columnBio: "Biografía",
  columnCategory: "Categoría",
  columnEmail: "Correo",
  columnWeb: "Web",
  view: "Ver",
  verifiedAccount: "Cuenta verificada",
  savedSearchesTitle: "Búsquedas guardadas",
  savedSearchesDescription: "Tus búsquedas guardadas",
  close: "Cerrar",
  noSavedSearches: "Todavía no tienes búsquedas guardadas",
  updatedOn: "Actualizada el {date}",
  loadSearch: "Cargar búsqueda",
  actionsLabel: "Acciones de {name}",
  renameSearch: "Renombrar búsqueda",
  deleteSearch: "Eliminar búsqueda",
  searchDeleted: "Búsqueda eliminada con éxito",
  couldNotDeleteSearch: "No se pudo eliminar la búsqueda",
  searchRenamed: "Búsqueda renombrada con éxito",
  couldNotRenameSearch: "No se pudo renombrar la búsqueda",
  cancel: "Cancelar",
  saving: "Guardando...",
  save: "Guardar",
  searchName: "Nombre de la búsqueda",
  searchNamePlaceholder: "p. ej. Influencers de fitness en español",
  nameTooShort: "El nombre debe tener al menos 3 caracteres",
  nameTooLong: "El nombre debe tener como máximo 50 caracteres",
  nameInvalid: "El nombre contiene caracteres no válidos",
  influencerNotFound: "Influencer no encontrado",
  backToSearch: "Volver a la búsqueda",
  contactEmail: "Correo",
  contactPhone: "Teléfono",
  contactWebsite: "Sitio web",
  viewOnInstagram: "Ver en Instagram",
  analyzing: "Analizando...",
  analyzeFollowers: "Analizar seguidores",
  verified: "Verificado",
  private: "Privada",
  public: "Pública",
  statFollowers: "Seguidores",
  statFollowing: "Seguidos",
  statPosts: "Publicaciones",
  contactInformation: "Información de contacto",
  noContactInformation: "No hay información de contacto disponible",
  categoriesAndTags: "Categorías y etiquetas",
  detailCategory: "Categoría",
  detailSubcategories: "Subcategorías",
  detailTags: "Etiquetas",
  profileDetails: "Detalles del perfil",
  detailCountry: "País",
  detailLanguage: "Idioma",
  detailGender: "Género",
  detailProfileType: "Tipo de perfil",
  detailAgeRange: "Rango de edad",
  detailAccountStatus: "Estado de la cuenta",
  performanceMetrics: "Métricas de rendimiento",
  metricAvgLikes: "Prom. de me gusta",
  metricAvgComments: "Prom. de comentarios",
  metricAvgViews: "Prom. de vistas",
  metricEngagementRate: "Tasa de interacción",
  metricQualityScore: "Puntuación de calidad",
  metricFakeFollowers: "Seguidores falsos",
  largeAccountTitle: "Cuenta grande",
  largeAccountDescription:
    "Las cuentas con más de {limit} seguidores suelen tener audiencias menos segmentadas. Recomendamos analizar cuentas más pequeñas para obtener mejores resultados.",
  analyzeAnyway: "Analizar de todos modos",
  couldNotAnalyze: "No se pudo crear el análisis",
};

export const FINDER_CATALOGS: Catalogs<FinderCatalog> = { en: EN, es: ES };

type FinderKey = PlainKey<FinderCatalog>;

export const ALL = "all";

/** The genders the filter offers, in the order it lists them. */
export const GENDER_FILTER: readonly Gender[] = ["unknown", "female", "male"];

export const GENDER_LABEL: Readonly<Record<Gender, FinderKey>> = {
  male: "genderMale",
  female: "genderFemale",
  unknown: "genderUnknown",
  brand: "genderBrand",
  group: "genderGroup",
  couple: "genderCouple",
  family: "genderFamily",
  band: "genderBand",
};

export const PROFILE_TYPE_LABEL: Readonly<Record<ProfileType, FinderKey>> = {
  creator: "profileCreator",
  business: "profileBusiness",
  personal: "profilePersonal",
};

export const SORT_ORDER: readonly FinderSort["by"][] = ["followers", "engagement", "avgLikes"];

export const SORT_LABEL: Readonly<Record<FinderSort["by"], FinderKey>> = {
  followers: "sortFollowers",
  engagement: "sortEngagement",
  avgLikes: "sortAvgLikes",
};

export const AI_EXAMPLES = [
  "aiExampleFitness",
  "aiExampleFoodies",
  "aiExampleBeauty",
] as const satisfies readonly FinderKey[];

/** The language names the contract ships; a language added there reads in its own English until it is added here. */
export const LANGUAGE_LABEL: Readonly<Record<string, FinderKey>> = {
  en: "languageEn",
  es: "languageEs",
  pt: "languagePt",
  it: "languageIt",
  fr: "languageFr",
  de: "languageDe",
  nl: "languageNl",
  tr: "languageTr",
  pl: "languagePl",
  ar: "languageAr",
};

/** Above this, an audience tends to be less targeted; the screen warns first. */
export const LARGE_ACCOUNT_FOLLOWERS = 700_000;

export const RANGE_KEYS = ["followers", "posts", "avgLikes", "avgComments", "engagementRate"] as const;
export type RangeKey = (typeof RANGE_KEYS)[number];

/** The filters as typed: bounds stay text ("100k") until searched. */
export type FinderDraft = Omit<FinderFilters, RangeKey> & Record<RangeKey, { min: string; max: string }>;

export function parseAmount(text: string): number | null {
  const match = text
    .trim()
    .toLowerCase()
    .replace(/[%\s]/g, "")
    .match(/^(\d+(?:[.,]\d+)?)(k|m)?$/);
  if (!match?.[1]) return null;
  const base = Number(match[1].replace(",", "."));
  return match[2] === "k" ? base * 1_000 : match[2] === "m" ? base * 1_000_000 : base;
}

export function formatAmount(value: number | null): string {
  if (value === null) return "";
  if (value >= 1_000_000 && value % 100_000 === 0) return `${value / 1_000_000}m`;
  if (value >= 1_000 && value % 100 === 0) return `${value / 1_000}k`;
  return String(value);
}

export function compactCount(value: number, format: Formats): string {
  return format.number(value, { notation: "compact", maximumFractionDigits: 1 });
}

export function toFilters(draft: FinderDraft): FinderFilters {
  const ranges = Object.fromEntries(
    RANGE_KEYS.map((key) => [key, { min: parseAmount(draft[key].min), max: parseAmount(draft[key].max) }]),
  ) as Pick<FinderFilters, RangeKey>;
  return { ...draft, ...ranges };
}

export function toDraft(filters: FinderFilters): FinderDraft {
  const ranges = Object.fromEntries(
    RANGE_KEYS.map((key) => [
      key,
      { min: formatAmount(filters[key].min), max: formatAmount(filters[key].max) },
    ]),
  ) as Pick<FinderDraft, RangeKey>;
  return { ...filters, ...ranges };
}

/** The name of a finder language in the active language; a code the table lacks reads as the contract names it. */
export function languageName(
  code: string,
  params: { readonly fallback: string; readonly t: Translate<FinderCatalog> },
): string {
  const key = LANGUAGE_LABEL[code];
  return key ? params.t(key) : params.fallback;
}
